// ==========================================================
// Thresholds Service — Species-Aware
// ==========================================================

import type { Thresholds, ThresholdRange, MushroomSpecies, CultivationStage } from '../types';
import { HEALTH_LABELS } from './strings';

// ── Mushroom Species Registry ──

export const MUSHROOM_SPECIES: MushroomSpecies[] = [
  { id: 'lion-mane',    name: 'Aslan Yelesi',    latinName: 'Hericium erinaceus',        prefix: 'LM', emoji: '🦁', color: 'text-amber-400' },
  { id: 'reishi',       name: 'Reishi',          latinName: 'Ganoderma lucidum',         prefix: 'RE', emoji: '✨', color: 'text-red-400' },
  { id: 'shiitake',     name: 'Shiitake',        latinName: 'Lentinula edodes',          prefix: 'SH', emoji: '🍄', color: 'text-orange-400' },
  { id: 'turkey-tail',  name: 'Hindi Kuyruğu',   latinName: 'Trametes versicolor',       prefix: 'HK', emoji: '🦃', color: 'text-teal-400' },
  { id: 'istiridye',    name: 'İstiridye',       latinName: 'Pleurotus ostreatus',       prefix: 'İS', emoji: '🌊', color: 'text-blue-400' },
  { id: 'karides',      name: 'Karides Mantarı', latinName: 'Pleurotus djamor',          prefix: 'KA', emoji: '🦐', color: 'text-pink-400' },
];

export function getSpeciesById(id: string): MushroomSpecies | undefined {
  return MUSHROOM_SPECIES.find(s => s.id === id);
}

// ── Species-Specific Thresholds (legacy single-stage — kept for backward compat) ──

export const SPECIES_THRESHOLDS: Record<string, Thresholds> = {
  'lion-mane': {
    temperature: { min: 15, max: 21, ideal: 18, unit: '°C', criticalLow: 10, criticalHigh: 25 },
    humidity:    { min: 85, max: 95, ideal: 90, unit: '%',  criticalLow: 75, criticalHigh: 99 },
    co2:         { min: 0, max: 1000, ideal: 500, unit: 'ppm', criticalHigh: 1200 },
  },
  'reishi': {
    temperature: { min: 20, max: 30, ideal: 25, unit: '°C', criticalLow: 15, criticalHigh: 35 },
    humidity:    { min: 80, max: 95, ideal: 90, unit: '%',  criticalLow: 70, criticalHigh: 99 },
    co2:         { min: 0, max: 2000, ideal: 1000, unit: 'ppm', criticalHigh: 3000 },
  },
  'shiitake': {
    temperature: { min: 12, max: 20, ideal: 16, unit: '°C', criticalLow: 8, criticalHigh: 24 },
    humidity:    { min: 80, max: 90, ideal: 85, unit: '%',  criticalLow: 70, criticalHigh: 95 },
    co2:         { min: 0, max: 1500, ideal: 800, unit: 'ppm', criticalHigh: 2000 },
  },
  'turkey-tail': {
    temperature: { min: 18, max: 24, ideal: 21, unit: '°C', criticalLow: 12, criticalHigh: 28 },
    humidity:    { min: 80, max: 95, ideal: 87, unit: '%',  criticalLow: 70, criticalHigh: 99 },
    co2:         { min: 0, max: 1200, ideal: 600, unit: 'ppm', criticalHigh: 1500 },
  },
  'istiridye': {
    temperature: { min: 10, max: 24, ideal: 18, unit: '°C', criticalLow: 5, criticalHigh: 28 },
    humidity:    { min: 80, max: 95, ideal: 90, unit: '%',  criticalLow: 70, criticalHigh: 99 },
    co2:         { min: 0, max: 1000, ideal: 500, unit: 'ppm', criticalHigh: 1500 },
  },
  'karides': {
    temperature: { min: 18, max: 30, ideal: 25, unit: '°C', criticalLow: 14, criticalHigh: 35 },
    humidity:    { min: 80, max: 95, ideal: 88, unit: '%',  criticalLow: 70, criticalHigh: 99 },
    co2:         { min: 0, max: 1200, ideal: 600, unit: 'ppm', criticalHigh: 1800 },
  },
};

// ── Species × Stage Targets (full matrix — primary source) ──
// VPD values are derived metrics (calculated from temp+RH pairs), not independent targets.
// Harvest is an EVENT not a stage — after harvest, system returns to spawn_run for next flush.
//
// Lion's Mane (H. erinaceus) parameters validated 2026-05-23 via:
//   Gemini, ChatGPT, Claude deep research + NotebookLM (Stamets, Chen 2017, commercial SOP consensus)
// Key constraint: Lion's Mane is 2× more CO₂-sensitive than oyster. Hard ceiling 1000 ppm fruiting.

export const SPECIES_STAGE_TARGETS: Record<string, Record<CultivationStage, Thresholds>> = {
  'lion-mane': {
    // Spawn Run: bags are sealed — tent ambient RH 65-78% is correct (bag maintains own microclimate).
    // CO₂ values = tent ambient (in-bag levels are 5000-40000 ppm, not measurable/controllable).
    spawn_run: {
      temperature:     { min: 20, max: 26, ideal: 23, unit: '°C', criticalLow: 17, criticalHigh: 28 },
      humidity:        { min: 65, max: 78, ideal: 72, unit: '%',  criticalLow: 55, criticalHigh: 85 },
      co2:             { min: 400, max: 2000, ideal: 800, unit: 'ppm', criticalHigh: 2500 },
      vpd:             { min: 0.55, max: 0.95, ideal: 0.76, unit: 'kPa', criticalHigh: 1.4 },
      dewPoint:        { min: 13, max: 20, ideal: 17, unit: '°C' },
      humidityDeficit: { min: 4.5, max: 8.5, ideal: 6.5, unit: 'g/m³' },
    },
    // Pinning: combined cold shock (6-8°C delta from spawn_run) + FAE to <800 ppm + RH spike to 95%+.
    // Temperature delta more important than absolute value. Primordia are desiccation-sensitive: RH floor is critical.
    pinning: {
      temperature:     { min: 12, max: 18, ideal: 14, unit: '°C', criticalLow: 8, criticalHigh: 22 },
      humidity:        { min: 92, max: 100, ideal: 95, unit: '%',  criticalLow: 80, criticalHigh: 100 },
      co2:             { min: 400, max: 800, ideal: 600, unit: 'ppm', criticalHigh: 1200 },
      vpd:             { min: 0.04, max: 0.12, ideal: 0.08, unit: 'kPa', criticalHigh: 0.20 },
      dewPoint:        { min: 11, max: 18, ideal: 14, unit: '°C' },
      humidityDeficit: { min: 0.2, max: 1.0, ideal: 0.5, unit: 'g/m³' },
    },
    // Fruiting: HARD CO₂ CEILING 1000 ppm — above this, coral-like branching morphology begins.
    // Above 1200 ppm = deformation guaranteed. VPD > 0.35 kPa → spine tip browning within hours.
    fruiting: {
      temperature:     { min: 15, max: 20, ideal: 18, unit: '°C', criticalLow: 10, criticalHigh: 24 },
      humidity:        { min: 85, max: 95, ideal: 90, unit: '%',  criticalLow: 75, criticalHigh: 98 },
      co2:             { min: 400, max: 950, ideal: 700, unit: 'ppm', criticalHigh: 1200 },
      vpd:             { min: 0.10, max: 0.28, ideal: 0.21, unit: 'kPa', criticalHigh: 0.45 },
      dewPoint:        { min: 12, max: 19, ideal: 16, unit: '°C' },
      humidityDeficit: { min: 0.8, max: 2.5, ideal: 1.6, unit: 'g/m³' },
    },
  },
  'reishi': {
    spawn_run: {
      temperature:     { min: 24, max: 28, ideal: 26, unit: '°C', criticalLow: 20, criticalHigh: 32 },
      humidity:        { min: 65, max: 75, ideal: 70, unit: '%',  criticalLow: 55, criticalHigh: 85 },
      co2:             { min: 400, max: 2000, ideal: 1000, unit: 'ppm', criticalHigh: 3000 },
      vpd:             { min: 0.8, max: 1.3, ideal: 1.0, unit: 'kPa', criticalHigh: 1.8 },
      dewPoint:        { min: 17, max: 21, ideal: 19, unit: '°C' },
      humidityDeficit: { min: 6, max: 10, ideal: 8, unit: 'g/m³' },
    },
    pinning: {
      temperature:     { min: 21, max: 26, ideal: 24, unit: '°C', criticalLow: 17, criticalHigh: 30 },
      humidity:        { min: 90, max: 95, ideal: 92, unit: '%',  criticalLow: 80, criticalHigh: 99 },
      co2:             { min: 2000, max: 5000, ideal: 3000, unit: 'ppm', criticalHigh: 8000 },
      vpd:             { min: 0.2, max: 0.4, ideal: 0.25, unit: 'kPa', criticalHigh: 0.7 },
      dewPoint:        { min: 19, max: 24, ideal: 22, unit: '°C' },
      humidityDeficit: { min: 1.0, max: 2.2, ideal: 1.5, unit: 'g/m³' },
    },
    fruiting: {
      temperature:     { min: 22, max: 28, ideal: 25, unit: '°C', criticalLow: 17, criticalHigh: 32 },
      humidity:        { min: 80, max: 90, ideal: 85, unit: '%',  criticalLow: 70, criticalHigh: 95 },
      co2:             { min: 1500, max: 3000, ideal: 2000, unit: 'ppm', criticalHigh: 5000 },
      vpd:             { min: 0.4, max: 0.8, ideal: 0.55, unit: 'kPa', criticalHigh: 1.2 },
      dewPoint:        { min: 18, max: 24, ideal: 21, unit: '°C' },
      humidityDeficit: { min: 2.5, max: 5.0, ideal: 3.5, unit: 'g/m³' },
    },
  },
  'shiitake': {
    spawn_run: {
      temperature:     { min: 22, max: 26, ideal: 24, unit: '°C', criticalLow: 18, criticalHigh: 30 },
      humidity:        { min: 60, max: 75, ideal: 68, unit: '%',  criticalLow: 50, criticalHigh: 85 },
      co2:             { min: 400, max: 1500, ideal: 800, unit: 'ppm', criticalHigh: 2500 },
      vpd:             { min: 0.7, max: 1.2, ideal: 0.95, unit: 'kPa', criticalHigh: 1.6 },
      dewPoint:        { min: 15, max: 19, ideal: 17, unit: '°C' },
      humidityDeficit: { min: 5.5, max: 9, ideal: 7, unit: 'g/m³' },
    },
    pinning: {
      temperature:     { min: 10, max: 16, ideal: 13, unit: '°C', criticalLow: 6, criticalHigh: 20 },
      humidity:        { min: 90, max: 95, ideal: 92, unit: '%',  criticalLow: 80, criticalHigh: 99 },
      co2:             { min: 400, max: 1000, ideal: 600, unit: 'ppm', criticalHigh: 1500 },
      vpd:             { min: 0.08, max: 0.25, ideal: 0.15, unit: 'kPa', criticalHigh: 0.5 },
      dewPoint:        { min: 9, max: 15, ideal: 12, unit: '°C' },
      humidityDeficit: { min: 0.4, max: 1.2, ideal: 0.8, unit: 'g/m³' },
    },
    fruiting: {
      temperature:     { min: 12, max: 18, ideal: 15, unit: '°C', criticalLow: 8, criticalHigh: 22 },
      humidity:        { min: 80, max: 90, ideal: 85, unit: '%',  criticalLow: 70, criticalHigh: 95 },
      co2:             { min: 500, max: 1200, ideal: 800, unit: 'ppm', criticalHigh: 2000 },
      vpd:             { min: 0.2, max: 0.45, ideal: 0.3, unit: 'kPa', criticalHigh: 0.8 },
      dewPoint:        { min: 10, max: 16, ideal: 13, unit: '°C' },
      humidityDeficit: { min: 1.3, max: 2.6, ideal: 1.9, unit: 'g/m³' },
    },
  },
  'turkey-tail': {
    spawn_run: {
      temperature:     { min: 22, max: 26, ideal: 24, unit: '°C', criticalLow: 18, criticalHigh: 30 },
      humidity:        { min: 65, max: 75, ideal: 70, unit: '%',  criticalLow: 55, criticalHigh: 85 },
      co2:             { min: 400, max: 1500, ideal: 800, unit: 'ppm', criticalHigh: 2500 },
      vpd:             { min: 0.7, max: 1.1, ideal: 0.9, unit: 'kPa', criticalHigh: 1.5 },
      dewPoint:        { min: 15, max: 19, ideal: 17, unit: '°C' },
      humidityDeficit: { min: 5.5, max: 8.5, ideal: 7, unit: 'g/m³' },
    },
    pinning: {
      temperature:     { min: 17, max: 22, ideal: 19, unit: '°C', criticalLow: 12, criticalHigh: 26 },
      humidity:        { min: 90, max: 95, ideal: 92, unit: '%',  criticalLow: 80, criticalHigh: 99 },
      co2:             { min: 400, max: 900, ideal: 600, unit: 'ppm', criticalHigh: 1400 },
      vpd:             { min: 0.15, max: 0.35, ideal: 0.22, unit: 'kPa', criticalHigh: 0.6 },
      dewPoint:        { min: 15, max: 20, ideal: 18, unit: '°C' },
      humidityDeficit: { min: 0.8, max: 1.8, ideal: 1.2, unit: 'g/m³' },
    },
    fruiting: {
      temperature:     { min: 18, max: 24, ideal: 21, unit: '°C', criticalLow: 13, criticalHigh: 28 },
      humidity:        { min: 82, max: 92, ideal: 87, unit: '%',  criticalLow: 70, criticalHigh: 96 },
      co2:             { min: 400, max: 1200, ideal: 700, unit: 'ppm', criticalHigh: 1800 },
      vpd:             { min: 0.25, max: 0.55, ideal: 0.38, unit: 'kPa', criticalHigh: 0.9 },
      dewPoint:        { min: 14, max: 20, ideal: 17, unit: '°C' },
      humidityDeficit: { min: 1.8, max: 3.4, ideal: 2.4, unit: 'g/m³' },
    },
  },
  // İstiridye mantarı (Pleurotus ostreatus) — geniş sıcaklık toleransı, hızlı fruiting
  'istiridye': {
    spawn_run: {
      temperature:     { min: 20, max: 26, ideal: 23, unit: '°C', criticalLow: 15, criticalHigh: 30 },
      humidity:        { min: 65, max: 78, ideal: 72, unit: '%',  criticalLow: 55, criticalHigh: 88 },
      co2:             { min: 400, max: 2000, ideal: 1000, unit: 'ppm', criticalHigh: 3000 },
      vpd:             { min: 0.5, max: 1.1, ideal: 0.8, unit: 'kPa', criticalHigh: 1.6 },
      dewPoint:        { min: 13, max: 19, ideal: 16, unit: '°C' },
      humidityDeficit: { min: 4.5, max: 8.5, ideal: 6.5, unit: 'g/m³' },
    },
    pinning: {
      temperature:     { min: 10, max: 18, ideal: 14, unit: '°C', criticalLow: 6, criticalHigh: 22 },
      humidity:        { min: 88, max: 96, ideal: 92, unit: '%',  criticalLow: 78, criticalHigh: 99 },
      co2:             { min: 400, max: 800, ideal: 550, unit: 'ppm', criticalHigh: 1200 },
      vpd:             { min: 0.05, max: 0.18, ideal: 0.10, unit: 'kPa', criticalHigh: 0.35 },
      dewPoint:        { min: 9, max: 17, ideal: 13, unit: '°C' },
      humidityDeficit: { min: 0.3, max: 1.2, ideal: 0.7, unit: 'g/m³' },
    },
    fruiting: {
      temperature:     { min: 10, max: 24, ideal: 18, unit: '°C', criticalLow: 6, criticalHigh: 28 },
      humidity:        { min: 80, max: 95, ideal: 88, unit: '%',  criticalLow: 70, criticalHigh: 99 },
      co2:             { min: 400, max: 1000, ideal: 600, unit: 'ppm', criticalHigh: 1500 },
      vpd:             { min: 0.12, max: 0.45, ideal: 0.25, unit: 'kPa', criticalHigh: 0.75 },
      dewPoint:        { min: 9, max: 20, ideal: 15, unit: '°C' },
      humidityDeficit: { min: 0.8, max: 3.0, ideal: 1.8, unit: 'g/m³' },
    },
  },
  // Karides mantarı / Pembe İstiridye (Pleurotus djamor) — tropikal, yüksek sıcaklık sever
  'karides': {
    spawn_run: {
      temperature:     { min: 24, max: 30, ideal: 27, unit: '°C', criticalLow: 20, criticalHigh: 34 },
      humidity:        { min: 65, max: 78, ideal: 72, unit: '%',  criticalLow: 55, criticalHigh: 88 },
      co2:             { min: 400, max: 2000, ideal: 1000, unit: 'ppm', criticalHigh: 3000 },
      vpd:             { min: 0.8, max: 1.4, ideal: 1.1, unit: 'kPa', criticalHigh: 2.0 },
      dewPoint:        { min: 16, max: 22, ideal: 19, unit: '°C' },
      humidityDeficit: { min: 5.5, max: 10, ideal: 7.5, unit: 'g/m³' },
    },
    pinning: {
      temperature:     { min: 20, max: 28, ideal: 24, unit: '°C', criticalLow: 16, criticalHigh: 32 },
      humidity:        { min: 88, max: 96, ideal: 92, unit: '%',  criticalLow: 78, criticalHigh: 99 },
      co2:             { min: 400, max: 900, ideal: 600, unit: 'ppm', criticalHigh: 1400 },
      vpd:             { min: 0.10, max: 0.35, ideal: 0.20, unit: 'kPa', criticalHigh: 0.60 },
      dewPoint:        { min: 18, max: 26, ideal: 22, unit: '°C' },
      humidityDeficit: { min: 0.5, max: 1.8, ideal: 1.0, unit: 'g/m³' },
    },
    fruiting: {
      temperature:     { min: 18, max: 30, ideal: 25, unit: '°C', criticalLow: 14, criticalHigh: 35 },
      humidity:        { min: 80, max: 95, ideal: 88, unit: '%',  criticalLow: 70, criticalHigh: 99 },
      co2:             { min: 400, max: 1200, ideal: 650, unit: 'ppm', criticalHigh: 1800 },
      vpd:             { min: 0.18, max: 0.55, ideal: 0.32, unit: 'kPa', criticalHigh: 0.90 },
      dewPoint:        { min: 16, max: 26, ideal: 21, unit: '°C' },
      humidityDeficit: { min: 1.0, max: 3.5, ideal: 2.0, unit: 'g/m³' },
    },
  },
};

/** Lookup stage-specific thresholds. Falls back to lion-mane or fruiting defaults. */
export function getStageTargets(speciesId: string, stage: CultivationStage): Thresholds {
  return (
    SPECIES_STAGE_TARGETS[speciesId]?.[stage] ??
    SPECIES_STAGE_TARGETS['lion-mane'][stage] ??
    SPECIES_STAGE_TARGETS['lion-mane']['fruiting']
  );
}

/** Map legacy TentPhase to CultivationStage */
export function phaseToStage(phase: 'fruiting' | 'incubation'): CultivationStage {
  return phase === 'incubation' ? 'spawn_run' : 'fruiting';
}

/** Map CultivationStage to health-score phase (spawn_run = incubation, pinning/fruiting = fruiting) */
export function stageToPhase(stage: CultivationStage): 'incubation' | 'fruiting' {
  return stage === 'spawn_run' ? 'incubation' : 'fruiting';
}

// ── Incubation readiness window per species (days from inoculation) ──
export const SPECIES_INCUBATION_DAYS: Record<string, { minDays: number; typicalDays: number }> = {
  'lion-mane':   { minDays: 14, typicalDays: 21 },
  'shiitake':    { minDays: 30, typicalDays: 60 },
  'reishi':      { minDays: 45, typicalDays: 90 },
  'turkey-tail': { minDays: 21, typicalDays: 35 },
  'istiridye':   { minDays: 10, typicalDays: 18 },
  'karides':     { minDays: 7,  typicalDays: 14 },
};

// ── Fruiting harvest window per species ──
// minDays: earliest harvest, idealDays: optimal, maxDays: overdue threshold
// Lion's Mane: harvest when spines 6-10mm, snow-white, before any yellowing at tips
// PIN-BASED window (from pinDetectedDate): tighter — spines 1-2cm takes ~3-5 days after first pin
export const SPECIES_PIN_WINDOW: Record<string, { minDays: number; idealDays: number; maxDays: number }> = {
  'lion-mane': { minDays: 3, idealDays: 5, maxDays: 7 },
};

export const SPECIES_FRUITING_WINDOW: Record<string, { minDays: number; idealDays: number; maxDays: number }> = {
  'lion-mane':   { minDays: 7,  idealDays: 10, maxDays: 14 },
  'shiitake':    { minDays: 8,  idealDays: 12, maxDays: 16 },
  'reishi':      { minDays: 30, idealDays: 45, maxDays: 60 },
  'turkey-tail': { minDays: 20, idealDays: 30, maxDays: 45 },
  'istiridye':   { minDays: 4,  idealDays: 6,  maxDays: 9  },
  'karides':     { minDays: 3,  idealDays: 5,  maxDays: 8  },
};

// ── Default (active species) thresholds ──
// Now stage-aware. Defaults to fruiting if no stage provided.
export function getActiveThresholds(primarySpeciesId: string = 'lion-mane', stage: CultivationStage = 'fruiting'): Thresholds {
  return getStageTargets(primarySpeciesId, stage);
}

// Legacy alias for backward compatibility
export const LIONS_MANE_THRESHOLDS = SPECIES_THRESHOLDS['lion-mane'];

// ── Overall health-score severity (HealthScore.overall → 0–100) ──
// Canonical thresholds matching daemon HealthScore semantics.
export const THRESHOLD_OK = 80;
export const THRESHOLD_WARN = 55;
export type Severity = 'ok' | 'warn' | 'crit';
export const severityForScore = (n: number): Severity =>
  n >= THRESHOLD_OK ? 'ok' : n >= THRESHOLD_WARN ? 'warn' : 'crit';

// ── Scoring ──

export function calculateMetricScore(value: number, range: ThresholdRange): number {
  if (value >= range.min && value <= range.max) {
    const distFromIdeal = Math.abs(value - range.ideal);
    const maxDist = Math.max(range.ideal - range.min, range.max - range.ideal);
    return Math.round(100 - (distFromIdeal / maxDist) * 20);
  }
  const overMin = range.min - value;
  const overMax = value - range.max;
  const over = Math.max(overMin, overMax);
  return Math.max(0, Math.round(80 - over * 8));
}

export function getScoreLabel(score: number): { label: string; color: string } {
  if (score >= 90) return { label: HEALTH_LABELS.excellent, color: '#34d399' };
  if (score >= 70) return { label: HEALTH_LABELS.good,      color: '#60a5fa' };
  if (score >= 50) return { label: HEALTH_LABELS.warning,   color: '#fbbf24' };
  return                  { label: HEALTH_LABELS.critical,  color: '#f87171' };
}

// ── Metric severity (for card coloring) ──

export function getMetricSeverity(
  metric: 'temperature' | 'humidity' | 'co2',
  value: number,
  speciesId = 'lion-mane',
  stage: import('../types').CultivationStage = 'fruiting',
): 'normal' | 'warning' | 'critical' {
  const thresholds = getStageTargets(speciesId, stage);
  const range = thresholds[metric];
  if (range.criticalHigh !== undefined && value >= range.criticalHigh) return 'critical';
  if (range.criticalLow !== undefined && value <= range.criticalLow) return 'critical';
  if (value < range.min || value > range.max) return 'warning';
  return 'normal';
}
